import { SnowflakeCompilerSqlTokenWords } from "@redotech/redo-analytics-custom-analytics/compilers/snowflake/v1/snowflake-query-compiler-sql-tokens";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";

export const TAB_QUERY_PARAM = "tab";
export const OVERVIEW_LOOKBACK_PARAM = "overviewLookbackDays";
export const OVERVIEW_TEAM_INPUT_PARAM = "overviewTeamInput";
export const OVERVIEW_TEAM_FILTER_PARAM = "overviewTeamId";
export const CUSTOM_REPORTS_DEBUG_PATHNAME = "/analytics/debug";
export const CUSTOM_REPORTS_CATALOG_PATHNAME = "/analytics/catalog";
export const CUSTOM_REPORTS_AUTHOR_PATHNAME = "/analytics/author";
export const DEBUG_REPORT_ID_PARAM = "debugReportId";
export const DEBUG_TEAM_ID_PARAM = "debugTeamId";
export const DEBUG_AUTO_LOAD_PARAM = "debugAutoLoad";
export const AUTHOR_TEAM_ID_PARAM = "authorTeamId";
export const AUTHOR_TARGET_REPORT_ID_PARAM = "authorTargetReportId";
export const AUTHOR_SOURCE_REPORT_ID_PARAM = "authorSourceReportId";
export const AUTHOR_AUTO_LOAD_PARAM = "authorAutoLoad";
export const AUTHOR_AST_STORAGE_KEY = "adminCustomReportsAuthorAst";
export const AUTHOR_SQL_STORAGE_KEY = "adminCustomReportsAuthorSql";
export const AUTHOR_PROMPT_STORAGE_KEY = "adminCustomReportsAuthorPrompt";
export const AUTHOR_PROMPT_CONTEXT_STORAGE_KEY =
  "adminCustomReportsAuthorPromptContext";
export const AUTHOR_MODE_PARAM = "authorMode";
export const AUTHOR_MERCHANT_PARITY_ATTEMPTS = 1;
export const AUTHOR_PROMPT_DEFAULT_ATTEMPTS = 3;
// Must not exceed the maxAttempts ceiling in the draftCustomReportAstFromPrompt schema.
export const AUTHOR_PROMPT_MAX_ATTEMPTS = 5;
export const AUTHOR_PREVIEW_ROW_LIMIT = 50;

export const QUERY_VERSION_PARAM = "queryVersion";

export const CATALOG_SEARCH_PARAM = "catalogSearch";
export const CATALOG_SEARCH_DEBOUNCE_MS = 200;
export const CATALOG_SEARCH_RESULT_LIMIT = 30;
export const RELATIONSHIPS_BASE_GRAIN_PARAM = "relationshipsBase";
export const RELATIONSHIPS_FIELD_PARAM = "relationshipsField";
export const RELATIONSHIPS_SEARCH_PARAM = "relationshipsSearch";
export const RELATIONSHIPS_SEARCH_DEBOUNCE_MS = 200;

export const CATALOG_METADATA_SECTION_IDS: ReadonlySet<string> = new Set([
  "default_filters",
  "column_filters",
  "restrictions",
  "relationships",
  "routes",
  "pins",
]);

export const CATALOG_RELATIONSHIP_SECTION_IDS: ReadonlySet<string> = new Set([
  "relationships",
  "routes",
  "pins",
]);

export const CATALOG_FIELD_SECTION_IDS: readonly string[] = [
  "attributes",
  "measures",
  "calculated_fields",
  "metrics",
  "cross_grain_metrics",
  "filters",
];

export const RELATIONSHIP_GRAPH_ACTIVE_COLOR =
  "var(--redo-colors-orange-dark-500)";
export const RELATIONSHIP_GRAPH_SUPPORT_COLOR =
  "var(--redo-colors-success-500)";

export const METADATA_COLLAPSED_PREVIEW_LENGTH = 220;

export interface CustomReportsLookbackWindow {
  lookbackDays: number;
  lookbackEndOffsetDays: number;
}

export const Last30DaysOption: RedoListItem<CustomReportsLookbackWindow> = {
  id: "30",
  text: "Last 30 days",
  type: "text",
  value: { lookbackDays: 30, lookbackEndOffsetDays: 0 },
};

export const lookbackOptions: RedoListItem<CustomReportsLookbackWindow>[] = [
  {
    id: "today",
    text: "Today",
    type: "text",
    value: { lookbackDays: 1, lookbackEndOffsetDays: 0 },
  },
  {
    id: "yesterday",
    text: "Yesterday",
    type: "text",
    value: { lookbackDays: 1, lookbackEndOffsetDays: 1 },
  },
  {
    id: "7",
    text: "Last 7 days",
    type: "text",
    value: { lookbackDays: 7, lookbackEndOffsetDays: 0 },
  },
  Last30DaysOption,
  {
    id: "90",
    text: "Last 90 days",
    type: "text",
    value: { lookbackDays: 90, lookbackEndOffsetDays: 0 },
  },
  {
    id: "365",
    text: "Last 365 days",
    type: "text",
    value: { lookbackDays: 365, lookbackEndOffsetDays: 0 },
  },
];

// Tokenizes enough JSON for readable syntax highlighting. JSON.parse still owns
// the actual string unescaping in the classifier.
export const JSON_CODE_TOKEN_PATTERN =
  /("(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null|[{}[\]:,])/g;

// Tokenizes enough Snowflake SQL for readable highlighting and catalog hovers.
// This is intentionally a highlighter pattern, not a full SQL parser.
export const SQL_CODE_TOKEN_PATTERN =
  /('(?:''|[^'])*'|"(?:""|[^"])*"|[A-Za-z_][A-Za-z0-9_$]*|-?\d+(?:\.\d+)?|<=|>=|<>|!=|[(),.*=+\-/])/g;

// Classifies scalar code tokens without re-running the larger tokenizer regexes.
export const NUMBER_CODE_TOKEN_PATTERN = /^-?\d/;
export const SQL_IDENTIFIER_START_PATTERN = /^[A-Za-z_]/;
export const SQL_OPERATOR_TOKEN_PATTERN = /^[(),.*=+\-/<>!]+$/;
export const WHITESPACE_PATTERN = /\s/;

// Snowflake escapes a double quote inside a quoted identifier by doubling it.
export const SQL_ESCAPED_IDENTIFIER_QUOTE_PATTERN = /""/g;

// Normalizes catalog lookup labels that may arrive quoted from JSON or SQL.
export const CATALOG_LOOKUP_OUTER_QUOTE_PATTERN = /^["']|["']$/g;
export const CATALOG_LOOKUP_DOUBLE_QUOTE_PATTERN = /\\?"/g;

// Converts internal catalog keys into readable labels.
export const CAMEL_CASE_BOUNDARY_PATTERN = /([a-z0-9])([A-Z])/g;
export const UNDERSCORE_PATTERN = /_/g;
export const WORD_INITIAL_PATTERN = /\b\w/g;

export const SqlKeywords = new Set(SnowflakeCompilerSqlTokenWords);

export const CatalogLookupStopwords = new Set([
  "all",
  "and",
  "any",
  "array",
  "as",
  "by",
  "date",
  "false",
  "field",
  "for",
  "id",
  "in",
  "item",
  "kind",
  "label",
  "metric",
  "name",
  "null",
  "number",
  "of",
  "on",
  "ref",
  "table",
  "text",
  "true",
  "type",
  "value",
]);

export const CatalogLookupDetailKeys = new Set([
  "baseTable",
  "field",
  "grain",
  "namespace",
  "owningTable",
  "physicalFieldName",
  "physicalTable",
  "refKey",
  "sourceTable",
  "table",
  "value",
]);
